import React, { useRef } from 'react';
import {
  Printer,
  X,
  FileCheck,
  ShieldCheck,
  ArrowDownLeft,
  ArrowUpRight,
} from 'lucide-react';
import type { WalletTransaction } from '../types';

interface TransactionReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: WalletTransaction | null;
  clientName?: string;
  clientTaxId?: string;
  clientEmail?: string;
  currency?: string;
}

export const TransactionReceiptModal: React.FC<TransactionReceiptModalProps> = ({
  isOpen,
  onClose,
  transaction,
  clientName = 'Cliente do Gateway',
  clientTaxId = '999999999',
  clientEmail = 'financeiro@empresa.ao',
  currency = 'AOA',
}) => {
  const receiptRef = useRef<HTMLDivElement>(null);

  if (!isOpen || !transaction) return null;

  const dateObj = transaction.created_at ? new Date(transaction.created_at) : new Date();
  const year = dateObj.getFullYear();
  const receiptNumber = `FT-REC-${year}-${transaction.id.slice(0, 8).toUpperCase()}`;
  const issueDate = dateObj.toLocaleDateString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const formatCurrency = (val: number) => {
    return `${Number(val || 0).toLocaleString('pt-AO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency}`;
  };

  const handlePrint = () => {
    window.print();
  };

  const isCredit = transaction.type === 'credit';
  const amount = Number(transaction.amount) || 0;
  // Tax / IVA simulation in Angola (14% standard or digital services)
  const ivaRate = 0.14;
  const subtotal = isCredit ? amount / (1 + ivaRate) : amount;
  const ivaAmount = isCredit ? amount - subtotal : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs overflow-y-auto print:p-0 print:bg-white print:static">
      <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-2xl w-full border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150 my-8 print:border-none print:shadow-none print:max-w-none print:w-full print:m-0 print:bg-white">
        {/* Action Header - hidden when printing */}
        <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between print:hidden">
          <div className="flex items-center gap-2.5">
            <div
              className={`p-2 rounded-xl ${
                isCredit
                  ? 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400'
                  : 'bg-primary-50 dark:bg-primary-950/50 text-primary-600 dark:text-primary-400'
              }`}
            >
              <FileCheck className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white text-base">
                {isCredit ? 'Recibo Oficial de Carregamento' : 'Nota de Débito / Consumo de API'}
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Documento Comercial & Contabilístico Válido
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="btn-base btn-sm btn-primary gap-1.5 shadow-sm"
              title="Imprimir ou Guardar em PDF"
            >
              <Printer className="w-4 h-4" />
              <span>Imprimir / PDF</span>
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Printable Receipt Body */}
        <div ref={receiptRef} className="p-8 sm:p-10 space-y-8 print:p-6 print:text-black">
          {/* Header & Logo */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-6 border-b border-slate-200 dark:border-slate-800 print:border-slate-300">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-8 h-8 rounded-lg bg-primary-600 text-white font-bold flex items-center justify-center text-sm">
                  TS
                </span>
                <span className="text-xl font-black tracking-tight text-slate-900 dark:text-white print:text-black">
                  TS API GATEWAY
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 print:text-slate-600">
                Tatu Solutions, Lda. · NIF: 5417281920
              </p>
              <p className="text-2xs text-slate-400 dark:text-slate-500 print:text-slate-500">
                Luanda, República de Angola
              </p>
            </div>
            <div className="text-left sm:text-right">
              <span
                className={`inline-block px-3 py-1 rounded-full text-xs font-bold font-mono border ${
                  isCredit
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 print:border-slate-400'
                    : 'bg-slate-100 text-slate-700 border-slate-200 print:border-slate-400'
                }`}
              >
                {receiptNumber}
              </span>
              <p className="text-xs text-slate-500 dark:text-slate-400 print:text-slate-600 mt-1">
                Data: {issueDate}
              </p>
            </div>
          </div>

          {/* Parties: Issuer & Client */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-xs">
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800 print:bg-white print:border-slate-300">
              <span className="text-2xs font-bold uppercase tracking-wider text-slate-400 block mb-1">
                Entidade Emissora
              </span>
              <strong className="text-slate-900 dark:text-white print:text-black block text-sm">
                Tatu Solutions, Lda.
              </strong>
              <p className="text-slate-600 dark:text-slate-300 print:text-slate-700 mt-1">
                Plataforma de Intermediação & Gateway de APIs
              </p>
              <p className="text-slate-500 font-mono">NIF: 5417281920</p>
              <p className="text-slate-500">suporte@tatusolutions.com</p>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800 print:bg-white print:border-slate-300">
              <span className="text-2xs font-bold uppercase tracking-wider text-slate-400 block mb-1">
                Adquirente / Cliente
              </span>
              <strong className="text-slate-900 dark:text-white print:text-black block text-sm">
                {clientName}
              </strong>
              <p className="text-slate-600 dark:text-slate-300 print:text-slate-700 mt-1">
                NIF: <span className="font-mono font-semibold">{clientTaxId}</span>
              </p>
              <p className="text-slate-500 truncate">{clientEmail}</p>
              <p className="text-slate-500">Angola</p>
            </div>
          </div>

          {/* Line Item Table */}
          <div className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden print:border-slate-300">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-100 dark:bg-slate-800/70 text-slate-600 dark:text-slate-300 font-bold uppercase tracking-wider text-2xs print:bg-slate-100 print:text-black">
                <tr>
                  <th className="p-3.5">Descrição da Operação</th>
                  <th className="p-3.5 text-center">Tipo</th>
                  <th className="p-3.5 text-center">Estado</th>
                  <th className="p-3.5 text-right">Valor Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 print:divide-slate-300">
                <tr>
                  <td className="p-3.5 font-medium text-slate-900 dark:text-white print:text-black">
                    <div className="font-bold">
                      {transaction.description || 'Operação de Saldo'}
                    </div>
                    <div className="text-2xs text-slate-400 font-mono mt-0.5">
                      ID: {transaction.id}
                    </div>
                  </td>
                  <td className="p-3.5 text-center font-mono">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-bold ${
                        isCredit
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                          : 'bg-primary-100 text-primary-800 dark:bg-primary-950/60 dark:text-primary-300'
                      }`}
                    >
                      {isCredit ? (
                        <>
                          <ArrowDownLeft className="w-3 h-3" /> Crédito
                        </>
                      ) : (
                        <>
                          <ArrowUpRight className="w-3 h-3" /> Débito
                        </>
                      )}
                    </span>
                  </td>
                  <td className="p-3.5 text-center">
                    <span className="badge-modern badge-success text-2xs">
                      {transaction.status === 'completed'
                        ? 'Liquidado'
                        : transaction.status === 'pending'
                          ? 'Pendente'
                          : 'Falhado'}
                    </span>
                  </td>
                  <td className="p-3.5 text-right font-mono font-bold text-slate-900 dark:text-white print:text-black text-sm">
                    {formatCurrency(amount)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Summary / Tax Breakdown */}
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4 pt-2">
            <div className="text-xs text-slate-500 space-y-1 max-w-sm">
              <p className="font-semibold text-slate-700 dark:text-slate-300 print:text-slate-800">
                Enquadramento Fiscal:
              </p>
              <p className="text-2xs text-slate-400 print:text-slate-600">
                Processado nos termos do Regime Geral de Faturação de Serviços Digitais. Os débitos
                correspondem ao consumo acumulado de quotas de APIs contratadas.
              </p>
            </div>

            <div className="w-full sm:w-64 space-y-2 text-xs">
              {isCredit && (
                <>
                  <div className="flex justify-between text-slate-600 dark:text-slate-400 print:text-slate-700">
                    <span>Incidência (Base):</span>
                    <span className="font-mono">{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600 dark:text-slate-400 print:text-slate-700">
                    <span>IVA (14%):</span>
                    <span className="font-mono">{formatCurrency(ivaAmount)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between pt-2 border-t border-slate-200 dark:border-slate-800 print:border-slate-400 text-sm font-bold text-slate-900 dark:text-white print:text-black">
                <span>Total Liquidado:</span>
                <span className="font-mono text-base text-primary-600 dark:text-primary-400 print:text-black">
                  {formatCurrency(amount)}
                </span>
              </div>
            </div>
          </div>

          {/* Legal / Security Footer */}
          <div className="pt-6 border-t border-dashed border-slate-200 dark:border-slate-800 print:border-slate-300 flex flex-col sm:flex-row items-center justify-between gap-3 text-2xs text-slate-400 print:text-slate-600">
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-emerald-500" />
              <span>
                Documento processado por programa de faturação certificado nº 382/AGT/2026
              </span>
            </div>
            <div className="font-mono text-center sm:text-right">
              Hash: {transaction.id.replace(/-/g, '').slice(0, 16).toUpperCase()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
